'use client';

import { convertToLocale } from '@lib/util/money';
import { ImageBadge } from '@modules/common/components/image-badge';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import type {
  ProductActionsVariantVM,
  ProductActionsVM,
} from 'types/view-models';

import { findEligiblePrice } from './bnpl-utils';

const KlarnaPlansModal = dynamic(
  () => import('./klarna-plans-modal').then((m) => m.KlarnaPlansModal),
  { ssr: false },
);

const ClearpayInfoModal = dynamic(
  () => import('./clearpay-info-modal').then((m) => m.ClearpayInfoModal),
  { ssr: false },
);

type BuyNowPayLaterProps = {
  product: ProductActionsVM;
  selectedVariant?: ProductActionsVariantVM;
};

export function BuyNowPayLater({
  product,
  selectedVariant,
}: BuyNowPayLaterProps) {
  const [plansOpen, setPlansOpen] = useState(false);
  const [clearpayOpen, setClearpayOpen] = useState(false);

  const eligible = findEligiblePrice(product, selectedVariant);
  if (!eligible) return null;

  const { amount, currencyCode } = eligible;

  const clearpayInstallment = convertToLocale({
    amount: amount / 4,
    currency_code: currencyCode,
  });

  const klarnaInstallment = convertToLocale({
    amount: amount / 3,
    currency_code: currencyCode,
  });

  const totalPrice = convertToLocale({ amount, currency_code: currencyCode });

  return (
    <>
      <div className="flex flex-col items-center gap-y-2 rounded-lg bg-ui-bg-subtle px-4 py-3 text-sm text-center">
        <div className="flex items-center gap-y-0.5 gap-x-1.5 flex-wrap justify-center">
          <span>
            Pay in 4 interest-free payments of{' '}
            <span translate="no">{clearpayInstallment}</span> with
          </span>
          <ImageBadge
            src="/payment-icons/clearpay.svg"
            alt="Clearpay"
            className="w-[72px] h-[24px] aspect-auto! bg-[#B2FCE4] border-transparent shrink-0 desktop:w-[72px]! desktop:flex-none!"
            imageClassName="p-1! mt-[1px]"
          />
          <button
            type="button"
            onClick={() => setClearpayOpen(true)}
            className="font-semibold cursor-pointer underline underline-offset-2 decoration-dotted text-ui-fg-base"
          >
            See details
          </button>
        </div>

        <div className="flex items-center gap-y-0.5 gap-x-1.5 flex-wrap justify-center">
          <span>
            3 interest-free payments of{' '}
            <span translate="no">{klarnaInstallment}</span> with
          </span>
          <ImageBadge
            src="/payment-icons/klarna.svg"
            alt="Klarna"
            className="w-[55px] h-[24px] aspect-auto! bg-[#ffb3c7] border-transparent shrink-0 desktop:w-[55px]! desktop:flex-none!"
            imageClassName="p-1.5!"
          />
          <button
            type="button"
            onClick={() => setPlansOpen(true)}
            className="font-semibold cursor-pointer underline underline-offset-2 decoration-dotted text-ui-fg-base"
          >
            See plans
          </button>
          <span className="text-ui-fg-muted">
            18+, T&Cs apply. Credit subject to status.
          </span>
        </div>
      </div>

      {plansOpen && (
        <KlarnaPlansModal
          isOpen={plansOpen}
          onClose={() => setPlansOpen(false)}
          klarnaInstallment={klarnaInstallment}
          totalPrice={totalPrice}
        />
      )}
      {clearpayOpen && (
        <ClearpayInfoModal
          isOpen={clearpayOpen}
          onClose={() => setClearpayOpen(false)}
        />
      )}
    </>
  );
}
